import firebase from '../../firebase-integration/index';
import { IIndividualFormData, ILeaderFormData, IUserCredentialData } from '../interfaces/user-profile';
import { setupUserProfileData, setupLeaderProfileData } from '../index';
import { updateUserProfile } from '../../store/users/actions';
import { deleteGroupsForUser } from '../groups/service';
import { deleteAllPostsForUser } from '../posts';

const db = firebase.firestore();
const usersCollection = db.collection('users');

interface IEmailSignInCredentials {
  email: string;
  password: string;
}

export const signInUserViaEmailPassword = ({ email, password }: IEmailSignInCredentials) => {
  if (!email || !password) {
    throw new Error();
  }

  return firebase.auth().signInWithEmailAndPassword(email, password);
};

export const signOutUser = () => {
  return firebase.auth().signOut();
};

export const editAccountEmail = async (formValues: any) => {
  try {
    const user = firebase.auth().currentUser;
    if (user) {
      await user.updateEmail(formValues['email']);
      await updateUserProfile(user, formValues);
      await sendEmailVerification();
    }
  } catch (e) {
    throw e;
  }
};

export const sendEmailVerification = async () => {
  try {
    const user = firebase.auth().currentUser;
    if (user) {
      await user.sendEmailVerification();
    }
  } catch (error) {
    throw error;
  }
};

export const sendPasswordResetEmail = async (email: string) => {
  try {
    await firebase.auth().sendPasswordResetEmail(email);
  } catch (error) {
    throw error;
  }
};

export const registerIndividualUser = async (regData: IIndividualFormData) => {
  try {
    const result = await firebase
      .auth()
      .createUserWithEmailAndPassword(regData.email, regData.password);

    if (!result.user) {
      throw new Error('Problem registering User');
    }

    await result.user?.updateProfile({
      displayName: regData.displayName,
    });

    if (!result.user.displayName) {
      throw new Error('Problem updating Registered User');
    }

    return await setupUserProfileData({
      ...regData,
      uid: result.user.uid,
      displayName: result.user.displayName,
    });
  } catch (error) {
    throw error;
  }
};

export const registerLeaderUser = async (regData: ILeaderFormData) => {
  try {
    const result = await firebase
      .auth()
      .createUserWithEmailAndPassword(regData.email, regData.password);

    if (!result.user) {
      throw new Error('Problem registering Leader');
    }

    await result.user?.updateProfile({
      displayName: regData.leaderName,
    });

    if (!result.user.displayName) {
      throw new Error('Problem updating Registered Leader');
    }

    return await setupLeaderProfileData({
      ...regData,
      uid: result.user.uid,
      displayName: result.user.displayName,
    });
  } catch (error) {
    throw error;
  }
};

export const deleteUser = async (userCredentials : IUserCredentialData) => {
  try {
    const user = firebase.auth().currentUser;
    const credentials = firebase.auth.EmailAuthProvider.credential(
        userCredentials.email,
        userCredentials.password);
    if (user && credentials) {
      const userId = user?.uid;
      user.reauthenticateWithCredential(credentials)
          .then(() => user?.delete())
          .then(async () => {
            await deleteGroupsForUser(userId);
            await deleteAllPostsForUser(userId);
            await usersCollection.doc(userId).delete();
          })
          .catch((error) => {
            throw error;
          });
    }
  } catch (error) {
    throw error;
  }
};
